import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { apiClient } from '../../utils/apiClient';
const fadeUp = keyframes `
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px 80px;

  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;
const Container = styled.div `
  width: 100%;
  max-width: 480px;
  animation: ${fadeUp} 0.8s ease-out;
`;
const BrandSection = styled.div `
  text-align: center;
  margin-bottom: 40px;
`;
const BrandTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: 0.05em;

  span { color: var(--accent-gold, #B8860B); }
`;
const BrandSubtitle = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
const Card = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--accent-gold, #B8860B) 20%, var(--accent-gold, #B8860B) 80%, transparent);
  }

  @media (max-width: 480px) { padding: 28px 20px; }
`;
const FormHeader = styled.div `
  text-align: center;
  margin-bottom: 32px;
`;
const FormTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: 0.02em;
`;
const FormSubtitle = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;
const FormGroup = styled.div `
  margin-bottom: 20px;
`;
const Label = styled.label `
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const Input = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.3s ease;

  &::placeholder { color: var(--text-tertiary); }

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.1));
  }
`;
const ErrorMessage = styled.div `
  color: var(--error-color, #ef4444);
  padding: 12px 16px;
  border: 1px solid var(--error-color, #ef4444);
  background: var(--error-bg, rgba(239, 68, 68, 0.1));
  margin-bottom: 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  line-height: 1.6;
`;
const SuccessMessage = styled.div `
  color: var(--success-color, #22c55e);
  padding: 12px 16px;
  border: 1px solid var(--success-color, #22c55e);
  background: var(--success-bg, rgba(34, 197, 94, 0.1));
  margin-bottom: 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  line-height: 1.6;
`;
const SubmitButton = styled.button `
  width: 100%;
  padding: 16px 32px;
  background: var(--accent-gold, #B8860B);
  color: white;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: var(--accent-gold-dark, #8B6914);
    transform: translateY(-2px);
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const FormFooter = styled.div `
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
  text-align: center;
`;
const FooterLink = styled.a `
  color: var(--accent-gold, #B8860B);
  text-decoration: none;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;
const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uid = useMemo(() => searchParams.get('uid') || '', [searchParams]);
    const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
    const hasLink = Boolean(uid && token);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        const res = await apiClient.post('auth-reset-password', { uid, token, password }, { skipAuth: true });
        setSubmitting(false);
        if (res.ok) {
            setDone(true);
        }
        else {
            setError(res.error || 'This password reset link is invalid or has expired. Please request a new one.');
        }
    };
    return (_jsx(PageWrapper, { children: _jsxs(Container, { children: [_jsxs(BrandSection, { children: [_jsxs(BrandTitle, { children: ["Royal ", _jsx("span", { children: "Gems" })] }), _jsx(BrandSubtitle, { children: "Wholesale Diamond Trading" })] }), _jsxs(Card, { children: [_jsxs(FormHeader, { children: [_jsx(FormTitle, { children: "Set a New Password" }), _jsx(FormSubtitle, { children: "Choose a strong password you haven't used before." })] }), !hasLink ? (_jsxs(_Fragment, { children: [_jsx(ErrorMessage, { children: "This reset link is missing required information. Please request a new password reset link." }), _jsx(SubmitButton, { type: "button", onClick: () => navigate('/forgot-password'), children: "Request New Link" })] })) : done ? (_jsxs(_Fragment, { children: [_jsx(SuccessMessage, { children: "Your password has been reset successfully. For your security, you've been signed out of all devices. You can now sign in with your new password." }), _jsx(SubmitButton, { type: "button", onClick: () => navigate('/login'), children: "Go to Sign In" })] })) : (_jsxs("form", { onSubmit: handleSubmit, children: [_jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "password", children: "New Password" }), _jsx(Input, { id: "password", type: "password", value: password, onChange: (e) => setPassword(e.target.value), placeholder: "At least 8 characters", required: true, autoFocus: true })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "confirm", children: "Confirm New Password" }), _jsx(Input, { id: "confirm", type: "password", value: confirm, onChange: (e) => setConfirm(e.target.value), placeholder: "Re-enter your password", required: true })] }), error && _jsx(ErrorMessage, { children: error }), _jsx(SubmitButton, { type: "submit", disabled: submitting, children: submitting ? 'Resetting...' : 'Reset Password' })] })), _jsx(FormFooter, { children: _jsx(FooterLink, { onClick: () => navigate('/login'), children: "Back to Sign In" }) })] })] }) }));
};
export default ResetPassword;
